import './Slides.scss';

import Parser from 'html-react-parser';
import React, { useEffect, useMemo, useState } from 'react';
import Slider from 'react-slick';

import ExternalLink from '@/common/components/atoms/ExternalLink/ExternalLink';
import Picture from '@/common/components/atoms/Picture/Picture';
import Video from '@/common/components/atoms/Video/Video';
import { NEXT, PREV } from '@/common/constants';
import getImageBySize from '@/common/helpers/getImageBySize';
import useSlides from '@/common/hooks/slides/useSlides';
import CustomArrow from '@/ebat/components/blocks/Common/Slider/CustomArrow/CustomArrow';
import { HOME_SLIDES_FORMATS } from '@/ebat/constants';

import { SlidesDefaultProps, SlidesPropTypes } from './Slides.proptypes';

const Slides = ({ className }) => {
  /**
   * Utility function to get the current device type based on window width
   * @returns {string} - Device type ('desktop', 'tablet', 'phone')
   */
  const getDeviceType = () => {
    const width = window.innerWidth;

    if (width >= 1024) {
      return 'desktop';
    }
    if (width >= 768) {
      return 'tablet';
    }
    return 'phone';
  };
  const { data: slides } = useSlides({});
  const [deviceType, setDeviceType] = useState(getDeviceType());

  useEffect(() => {
    const handleResize = () => {
      setDeviceType(getDeviceType());
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /**
   * Helper function to get the appropriate image based on device type
   * @param {Array} images - List of images
   * @param {string} device - Device type
   * @returns {string} - Image URL
   */
  const getImageForDevice = (images, device) => {
    let selectedImage = null;

    if (images) {
      if (device === 'desktop') {
        selectedImage = images.find((image) => image.destination === 'desktop');
      } else if (device === 'tablet') {
        selectedImage =
          images.find((image) => image.destination === 'tablet') ||
          images.find((image) => image.destination === 'desktop');
      } else if (device === 'phone') {
        selectedImage =
          images.find((image) => image.destination === 'phone') ||
          images.find((image) => image.destination === 'tablet') ||
          images.find((image) => image.destination === 'desktop');
      }
    }

    return selectedImage
      ? getImageBySize(
          selectedImage.url,
          HOME_SLIDES_FORMATS[selectedImage.destination],
        )
      : '';
  };

  /**
   * Get sliders settings
   * @type {object}
   */
  const getSliderSettings = useMemo(
    () => ({
      arrows: true,
      autoplay: true,
      autoplaySpeed: 5000,
      dots: true,
      fade: true,
      infinite: true,
      prevArrow: <CustomArrow type={PREV} />,
      nextArrow: <CustomArrow type={NEXT} />,
      pauseOnHover: true,
      slidesToShow: 1,
    }),
    [],
  );

  /**
   * Generate classNames
   * @type {string}
   */
  const classNames = useMemo(
    () => ['home-slides', className].filter((val) => val).join(' '),
    [className],
  );

  if (slides?.data.length > 0) {
    return (
      <div className={classNames}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <Slider {...getSliderSettings} className="home-slides__list">
          {slides.data.map((slide) => (
            <div
              className="home-slides__item"
              key={`home-slides-item-${slide.id}`}
              data-qa-id="home-slides-item"
            >
              {slide.link_video ? (
                <Video
                  className="home-slides__video"
                  src={slide.link_video}
                  title={slide.title}
                />
              ) : (
                <ExternalLink
                  className="home-slides__link"
                  href={slide.link}
                  context={`slide-${slide.title}`}
                  target={slide.target_blank ? '_blank' : '_self'}
                >
                  {slide.images?.length > 0 && (
                    <Picture
                      alt={slide.images[0].alt}
                      src={getImageForDevice(slide.images, deviceType)}
                      type="static"
                    />
                  )}
                </ExternalLink>
              )}
              <div className="home-slides__desc">
                {slide.description && Parser(slide.description)}
              </div>
            </div>
          ))}
        </Slider>
      </div>
    );
  }

  return null;
};

Slides.defaultProps = {
  ...SlidesDefaultProps,
};

Slides.propTypes = {
  ...SlidesPropTypes,
};

export default Slides;
